import styled from "@emotion/styled";
import ContactAgentDialog from "shared/components/contact-agent-dialog";
import SanitizeHtml from "shared/components/sanitize-html";
import React, { useEffect, useMemo, useState } from "react";
import { Grid, Tooltip } from "@mui/material";
import _, { replace } from "lodash";
import classnames from "classnames";
import { useRouter } from "next/router";
import CardAgent from "shared/components/agent-detail";
import { useTranslations } from "next-intl";
import getYouTubeVideoIdByUrl from "@helpers/youtube.helper";
import useLeadCountMutation from "custom-hooks/useLeadCountMutation";
import leadsCounterAdapter from "@endpoints/clients/lead counter/lead.counter.adapter";
import { C_LEAD_TYPE } from "shared/models/enums/lead_type.enum";
const rentRegex = new RegExp("Alquiler", "gi");
const saleRegex = new RegExp("Venta", "gi");

function getFeatures(data: any) {
  let features = [];
  if (data.parking_spots) {
    features.push({
      icon: "/icons/icon_car_remaxrd.svg",
      value: data.parking_spots,
      label: "Parqueo(s)",
    });
  }

  if (data.bathrooms) {
    features.push({
      icon: "/icons/icon_bathtub_remaxrd.svg",
      value: data.bathrooms,
      label: "Baños",
    });
  }

  if (data.bedrooms) {
    features.push({
      icon: "/icons/icon_bed_remaxrd.svg",
      value: data.bedrooms,
      label: "Habitación(es)",
    });
  }

  if (data.sqm_construction || data?.sqm_land) {
    features.push({
      icon: "/icons/icon_rule_remaxrd.svg",
      value: (data?.sqm_construction ?? data?.sqm_land) + " M\u00B2",
      label: "Metros cuadrados de construcción",
    });
  }
  if (data.floor_level || data.floors_total) {
    features.push({
      icon: "/icons/floors_remaxrd.svg",
      value: data.floor_level ?? data.floors_total,
      label: "Piso(s)",
    });
  }
  return features;
}

const handleClick = () => {
  const loanCalculator = document.getElementById("loanCalculator");
  loanCalculator?.scrollIntoView({ behavior: "smooth" });
};

function renderImprovements(data: any) {
  const improvements: any = groupImprovements(data);
  let col1 = [];
  let col2 = [];

  const categories = Object.keys(improvements);

  for (let index = 0; index < categories.length; index++) {
    const element = improvements[categories[index]];
    const category = categories[index];

    const domElement = (
      <DetailsImprovementsItem key={index}>
        <ImprovementItemTitle>{category}</ImprovementItemTitle>
        <ImprovementItemDescription>
          {element.values.join("\n")}
        </ImprovementItemDescription>
      </DetailsImprovementsItem>
    );

    if (col1.length < 5 && category !== "Mejoras") {
      col1.push(domElement);
    } else {
      col2.push(domElement);
    }
  }

  if (!col1.length) {
    return <DetailsImprovementColumns>{col2}</DetailsImprovementColumns>;
  } else {
    return (
      <>
        <DetailsImprovementColumns>{col1}</DetailsImprovementColumns>
        <DetailsImprovementColumns>{col2}</DetailsImprovementColumns>
      </>
    );
  }
}

function groupImprovements(data: any) {
  const { full_improvements = [] } = data;

  let groupImprovements: any = {};

  for (let index = 0; index < full_improvements.length; index++) {
    const element = full_improvements[index];
    const category = replace(
      replace(element.name, "&oacute;", "ó"),
      "&iacute;",
      "í"
    );

    const value = replace(element.value, "&iacute;", "í");

    if (groupImprovements.hasOwnProperty(category)) {
      groupImprovements[category].values.push(value);
    } else {
      groupImprovements[category] = {
        values: [value],
      };
    }
  }

  return groupImprovements;
}

const setVideoList = (
  videos: { id: number; realstate_id: number; path: string }[]
) =>
  videos
    ?.map((video) => ({
      ...video,
      path: getYouTubeVideoIdByUrl(video.path),
    }))
    .filter((video) => video.path)
    .map((video) => ({
      ...video,
      path: "https://youtube.com/embed/" + video.path,
    }));

function DetailsSection(props: { data: any; realestateId: number }) {
  const { ADD_LEAD_COUNT_M } = useLeadCountMutation();

  const { data, realestateId } = props;
  const [extraDataLayer, setExtraDataLayer] = useState<any>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] = useState<any>();
  const [clamped, setClamped] = useState<boolean>(true);
  const [clampedVideos, setClampedVideos] = useState(true);
  const [refDescription, setRefDescription] = useState<any>(null);
  const [showButton, setShowButton] = useState<boolean>(true);
  const handleToggleDescription = () => setClamped(!clamped);
  const handleToggleVideosClamp = () => setClampedVideos(!clampedVideos);
  const videosList = useMemo(() => setVideoList(data.videos), [data.videos]);

  const [agent, setAgent] = useState<any>(null);
  const router = useRouter();
  const translation = useTranslations("property");

  const sector = data.sector?.toLowerCase();
  const city = data.city?.toLowerCase();

  const renderSector = sector ? sector + ", " : "";

  const handleOpenModal = (agent: any) => {
    setOpen(true);
    setSelectedAgent(agent);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelectedAgent(null);
  };

  const onCallBackSubmitContactModal = () => {
    if (window) {
      const params = new URL(window.location.href).searchParams;
      const propertiesId = realestateId;
      const isAgencyReferer = params.get("areferer");
      const isAgentReferer = params.get("referer");
      const codeReferer = isAgencyReferer || isAgentReferer || selectedAgent;
      const isReferer = codeReferer ? true : false;
      if (propertiesId) {
        ADD_LEAD_COUNT_M({
          ...leadsCounterAdapter({
            isReferred: isReferer,
            realestateId: parseInt(`${propertiesId}`),
            idSelected: codeReferer ? parseInt(codeReferer!) : undefined,
            isAgentReferer: Boolean(isAgentReferer),
            isAgencyReferer: Boolean(isAgencyReferer),
            type: C_LEAD_TYPE.MAIL,
          }),
        });
      }
    }
  };

  useEffect(() => {
    const hasClamping = (el: any) => {
      const { clientHeight, scrollHeight } = el;
      return clientHeight !== scrollHeight;
    };

    const checkButtonAvailability = () => {
      if (refDescription?.current) {
        // Save current state to reapply later if necessary.
        const hadClampClass =
          refDescription?.current.classList.contains("clamp");
        // Make sure that CSS clamping is applied if aplicable.
        if (!hadClampClass) refDescription?.current.classList.add("clamp");
        // Check for clamping and show or hide button accordingly.
        setShowButton(hasClamping(refDescription?.current));
        // Sync clamping with local state.
        if (!hadClampClass) refDescription?.current.classList.remove("clamp");
      }
    };

    const debouncedCheck = _.debounce(checkButtonAvailability, 50);

    checkButtonAvailability();
    window.addEventListener("resize", debouncedCheck);

    return () => {
      window.removeEventListener("resize", debouncedCheck);
    };
  }, [refDescription]);

  // const loanBtnAnchor = any;

  function calculatorBtnAnchor() {
    if (
      data.business_type.search(saleRegex) !== -1 ||
      data.alternate_price > 0
    ) {
      return (
        <LoanCalculatorAnchor
          onClick={handleClick}
          className="calcula-tu-cuota"
        >
          <IconCalculator src="/icons/icon_calculator_remax.svg" />
          {translation("anchorToCalculator")}
        </LoanCalculatorAnchor>
      );
    }

    return null;
  }

  return (
    <Wrapper isRent={data.business_type.search(rentRegex) !== -1}>
      <DetailsProperty>
        <DetailsHeader>
          <DetailsTitle>
            <DetailsSubtitleLight>{sector}</DetailsSubtitleLight>
            {data.realstate_type}{" "}
          </DetailsTitle>
          <Grid container alignItems={"center"}>
            <DetailsSubtitle>
              {renderSector} {city}
            </DetailsSubtitle>

            {calculatorBtnAnchor()}
          </Grid>

          <DetailsFeatures>
            {getFeatures(data).map((item, index) => (
              <Tooltip
                key={index}
                title={item.label}
                arrow
                aria-label={item.label}
              >
                <DetailsFeaturesItem key={index}>
                  <DetailsFeaturesIcon src={item.icon} />
                  {item.value}
                </DetailsFeaturesItem>
              </Tooltip>
            ))}
            {data.realstate_type == "Proyecto" && data.trust_name && (
              <Fideo src="/icons/fideicomiso.svg" />
            )}
          </DetailsFeatures>
        </DetailsHeader>

        {data.description && (
          <DetailsDescription>
            <Title>{translation("description")}</Title>
            <SanitizeHtml
              className={classnames(clamped && "clamp")}
              html={data.description}
              setRefDescription={setRefDescription}
            />

            {showButton && (
              <ToggleDescription onClick={handleToggleDescription}>
                {clamped ? translation("readMore") : translation("readLess")}
              </ToggleDescription>
            )}
          </DetailsDescription>
        )}
        {videosList?.length ? (
          <DetailsDescription>
            <Title>{translation("videos.title")}</Title>
            <YoutubeEmbed src={videosList[0].path} />
            {!clampedVideos
              ? videosList
                  .slice(1)
                  .map((video: any) => (
                    <YoutubeEmbed key={video.id} src={video.path} />
                  ))
              : null}
            {videosList?.length > 1 ? (
              <>
                <br />
                <ToggleDescription onClick={handleToggleVideosClamp}>
                  {clampedVideos
                    ? translation("videos.see_more")
                    : translation("videos.see_less")}
                </ToggleDescription>
              </>
            ) : null}
          </DetailsDescription>
        ) : null}
        <DetailsImprovements>
          <Title>{translation("features")}</Title>
          <DetailsImprovementsContainer>
            {renderImprovements(data)}
          </DetailsImprovementsContainer>
        </DetailsImprovements>
      </DetailsProperty>

      <DetailsAgent>
        <CardAgent
          handleMessageButton={handleOpenModal}
          setExtraDataLayer={setExtraDataLayer}
        />
      </DetailsAgent>

      <ContactAgentDialog
        onCallBackSubmit={onCallBackSubmitContactModal}
        open={open}
        onClose={handleCloseModal}
        entity_info={
          router.query?.areferer
            ? { id: router.query?.areferer }
            : selectedAgent
        }
        isAgency={Boolean(router.query?.areferer ? 1 : 0)}
        realestateId={realestateId}
        classNameModalButton="enviar-formulario-propiedad"
        defaultMessage={translation("agentContact.defaultMessage")}
        extraDataLayer={extraDataLayer}
      />
    </Wrapper>
  );
}

const YoutubeEmbed = ({ src }: { src: string }) => (
  <YoutubeIframe
    src={src}
    title="YouTube video player"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    allowFullScreen
  />
);

const YoutubeIframe = styled.iframe`
  width: 100%;
  max-width: 560px;
  aspect-ratio: 16/9;
  border: none;
`;
const Fideo = styled.img`
  width: 140px;
  // margin-left: 10px;
  position: relative;
  bottom: -10px;
  height: auto;
`;

const Wrapper = styled.section<IWrapper>`
  display: flex;
  flex-wrap: wrap;
  padding-bottom: ${(props) => (props.isRent ? "0" : "100px")};
  justify-content: space-between;
`;

const DetailsProperty = styled.div`
  width: 65%;

  @media screen and (max-width: 991px) {
    width: 100%;
  }
`;

const DetailsHeader = styled.div`
  border-bottom: 1px solid #cbcbcb;
  margin-bottom: 30px;
`;

const DetailsTitle = styled.h1`
  font-size: ${36 / 16 + "rem"};
  font-weight: 500;
  margin-bottom: 10px;
  color: var(--color-blue-main);
  padding-right: 50px;
  line-height: 60px;

  @media screen and (max-width: 991px) {
    padding-right: 0;
  }
`;

const DetailsSubtitle = styled.p`
  font-size: ${17 / 16 + "rem"};
  color: var(----color-gray-01);
  font-weight: 500;
  text-transform: capitalize;
  display: inline-block;
  margin-right: 30px;
  @media screen and (max-width: 767px) {
    display: block;
    margin-bottom: 20px;
    margin-right: 0;
  }
`;

const LoanCalculatorAnchor = styled.span`
  border: 1px solid #29a9e0;
  border-radius: 50px;
  padding: 10px 15px;
  color: #29a9e0;
  font-weight: bold;
  font-size: 14px;
  text-transform: uppercase;
  line-height: 0;
  cursor: pointer;
  margin-top: 5px;
  display: inline-block;
`;

const IconCalculator = styled.img`
  vertical-align: middle;
  margin-right: 3px;
  max-width: 20px;
`;

const DetailsSubtitleLight = styled.span`
  font-weight: 300;
  text-transform: capitalize;
  margin-right: 10px;
`;

const DetailsFeatures = styled.ul`
  margin: 30px 0;
  display: flex;
  list-style: none;

  @media screen and (max-width: 385px) {
    flex-wrap: wrap;
  }
`;

const ToggleDescription = styled.span`
  border: 1px solid #29a9e0;
  border-radius: 50px;
  padding: 15px 20px;
  color: #29a9e0;
  font-weight: bold;
  font-size: 14px;
  text-transform: uppercase;
  line-height: 0;
  cursor: pointer;
  margin-top: 20px;
  display: inline-block;
`;

const DetailsFeaturesItem = styled.li`
  margin-right: 25px;
  font-weight: 500;
  font-size: ${13 / 16 + "rem"};
  color: #afb5bc;
  display: flex;
  align-items: flex-end;

  @media screen and (max-width: 385px) {
    margin-right: 20px;
    margin-top: 10px;
    margin-bottom: 10px;
  }
`;

const DetailsFeaturesIcon = styled.img`
  width: 35px;
  height: 35px;
  vertical-align: baseline;
  margin-bottom: -5px;
  margin-right: 10px;
`;

const Title = styled.h3`
  font-size: ${22 / 16 + "rem"};
  font-weight: 700;
  margin-bottom: 10px;
  color: var(--color-blue-main);
`;

const DetailsImprovements = styled.div``;

const DetailsImprovementsContainer = styled.div`
  display: grid;
  grid-template-columns: 50% 50%;
`;

const DetailsImprovementColumns = styled.div`
  display: grid;
  grid-template-columns: 100%;
  gap: 25px 0;
`;

const DetailsImprovementsItem = styled.div`
  width: 50%;
`;

const ImprovementItemTitle = styled.h4`
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 5px;
  color: var(--color-blue-main);
  text-transform: uppercase;
`;

const ImprovementItemDescription = styled.p`
  color: var(--color-gray-02);
  font-size: 1rem;
  font-weight: 500;
  white-space: break-spaces;
`;

const DetailsAgent = styled.div`
  width: 25%;
  padding-top: 150px;

  @media screen and (max-width: 991px) {
    width: 100%;
  }
`;

const DetailsDescription = styled.div`
  border-bottom: 1px solid #cbcbcb;
  margin-bottom: 30px;
  padding-bottom: 30px;
  p {
    margin: 10px 0px;
  }
  & .clamp {
    -webkit-line-clamp: 10;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    overflow-wrap: break-word;
  }
`;

interface IWrapper {
  isRent: boolean;
}

export default DetailsSection;
